import { useUser, useClerk } from '@clerk/react';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../lib/api';
import { ApiResponse } from '@vdrive/shared';

interface MeData {
  id: string;
  email: string;
  displayName: string | null;
  storageUsedBytes: number;
  plan: { id: string; name: string; storageBytes: number };
}

function fmt(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

const s = {
  nav: { position: 'sticky' as const, top: 0, zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 1.5rem', height: '56px', background: '#1a1a1a', color: '#fff', borderBottom: '1px solid #000' } as const,
  brand: { display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 700, fontSize: '1.125rem' } as const,
  right: { display: 'flex', alignItems: 'center', gap: '1.25rem', fontSize: '0.875rem' } as const,
  divider: { width: '1px', height: '18px', background: 'rgba(255,255,255,0.25)' } as const,
  storage: { opacity: 0.75, whiteSpace: 'nowrap' as const } as const,
  storageWarning: { opacity: 1, color: '#fca5a5', fontWeight: 600, whiteSpace: 'nowrap' as const } as const,
  warningBadge: { marginLeft: '0.5rem', padding: '0.1rem 0.45rem', background: '#dc2626', color: '#fff', borderRadius: '3px', fontSize: '0.7rem', fontWeight: 700 } as const,
  user: { opacity: 0.9, whiteSpace: 'nowrap' as const } as const,
  logoutBtn: { padding: '0.3rem 0.75rem', background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem', whiteSpace: 'nowrap' as const } as const,
};

export function Navigation(): JSX.Element {
  const { user: clerkUser } = useUser();
  const { signOut } = useClerk();
  const { data: me } = useQuery<MeData>({
    queryKey: ['me'],
    queryFn: async () => {
      const res = await apiGet<MeData>('/auth/me');
      return (res as ApiResponse<MeData>).data!;
    },
    enabled: !!clerkUser,
  });

  const displayName = clerkUser?.fullName || clerkUser?.primaryEmailAddress?.emailAddress || '';
  const usagePercent = me ? Math.min(100, Math.round((me.storageUsedBytes / me.plan.storageBytes) * 100)) : 0;
  const isNearLimit = usagePercent >= 90;

  return (
    <nav style={s.nav}>
      <span style={s.brand}>Vdrive</span>
      <div style={s.right}>
        {me && (
          <>
            <span style={isNearLimit ? s.storageWarning : s.storage}>
              {fmt(me.storageUsedBytes)} / {fmt(me.plan.storageBytes)} ({usagePercent}%)
              {isNearLimit && <span style={s.warningBadge}>容量残りわずか</span>}
            </span>
            <span style={s.divider} />
          </>
        )}
        <span style={s.user}>{displayName}</span>
        <button style={s.logoutBtn} onClick={() => signOut({ redirectUrl: '/login' })}>ログアウト</button>
      </div>
    </nav>
  );
}
